import { CurrencyPipe, DecimalPipe } from '@angular/common';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { BillingFrequency, Currency, MarketplaceBillingFrequency } from './display.price';

@Injectable()
export class DisplayPriceService {
  constructor(private translateService: TranslateService) {}

  public formatDisplayPrice(
    value: any,
    currency: Currency | string,
    billingFrequency?: BillingFrequency | MarketplaceBillingFrequency,
    isCents = true,
    translateZero = true,
    displaySymbol = true,
    isStartingPrice = false,
  ): Observable<string> {
    let display: 'code' | 'symbol' | 'symbol-narrow' | boolean = displaySymbol === true ? 'symbol' : 'code';
    let priceFormatFunc: (value: number, currency: string, display: string) => Observable<string> =
      this.defaultFormatFunc.bind(this);

    switch (currency) {
      case 'CZK':
        display = displaySymbol ? 'symbol-narrow' : 'code';
        priceFormatFunc = this.CZKFormatFunc.bind(this);
        break;
    }
    if (value && value > 0) {
      let price = Number(value);
      if (isCents) {
        price = this.convertCentsToDollars(price);
      }
      return priceFormatFunc(price, currency, display).pipe(
        switchMap((formattedPrice: string) => {
          if (billingFrequency) {
            return this.formatBillingFrequency(billingFrequency, formattedPrice);
          }
          return of(formattedPrice);
        }),
        switchMap((formattedPrice: string) => {
          if (isStartingPrice) {
            return this.translateService.stream('FRONTEND.CORE.SHARED.DISPLAY_PRICE.STARTING_AT_PRICE', {
              price: formattedPrice,
            });
          }
          return of(formattedPrice);
        }),
      );
    }
    if (translateZero) {
      if (value < 0) {
        return this.translateService.stream('FRONTEND.CORE.SHARED.DISPLAY_PRICE.CONTACT_SALES');
      }
      if (value === 0) {
        return this.translateService.stream('FRONTEND.CORE.SHARED.DISPLAY_PRICE.FREE');
      }
    }
    if (value === 0) {
      return priceFormatFunc(value, currency, display);
    }
    return this.translateService.stream('FRONTEND.CORE.SHARED.DISPLAY_PRICE.CONTACT_SALES');
  }

  private convertCentsToDollars(centValue: number): number {
    if (!centValue) {
      return centValue;
    }

    if (!Number.isInteger(centValue)) {
      centValue = Number(centValue);
      centValue = Math.round(centValue);
    }
    return centValue / 100;
  }

  public formatBillingFrequency(frequency: string, price?: string): Observable<string> {
    let returnValue!: Observable<string>;
    if (frequency) {
      switch (frequency.toLowerCase()) {
        case 'once':
        case 'one time':
          returnValue = this.translateService.stream('FRONTEND.CORE.SHARED.DISPLAY_PRICE.PRICING_FREQUENCIES.ONE_TIME');
          break;
        case 'daily':
          returnValue = this.translateService.stream('FRONTEND.CORE.SHARED.DISPLAY_PRICE.PRICING_FREQUENCIES.DAILY');
          break;
        case 'weekly':
          returnValue = this.translateService.stream('FRONTEND.CORE.SHARED.DISPLAY_PRICE.PRICING_FREQUENCIES.WEEKLY');
          break;
        case 'monthly':
          returnValue = this.translateService.stream('FRONTEND.CORE.SHARED.DISPLAY_PRICE.PRICING_FREQUENCIES.MONTHLY');
          break;
        case 'yearly':
          returnValue = this.translateService.stream('FRONTEND.CORE.SHARED.DISPLAY_PRICE.PRICING_FREQUENCIES.YEARLY');
          break;
        case 'other':
          returnValue = this.translateService.stream('FRONTEND.CORE.SHARED.DISPLAY_PRICE.PRICING_FREQUENCIES.OTHER');
          break;
        default:
          returnValue = of(frequency);
      }
    }
    if (price) {
      return returnValue.pipe(map((v) => `${price} ${v}`)) ?? of('');
    }
    return returnValue;
  }

  private defaultFormatFunc(value: number, currency: string, display: string): Observable<string> {
    if (!currency) {
      return of(new DecimalPipe('en-US').transform(value, '1.2-2') ?? '');
    } else {
      return of(new CurrencyPipe('en-US').transform(value, currency, display, '1.2-2') ?? '');
    }
  }

  private CZKFormatFunc(value: number, currency: string, display: string): Observable<string> {
    const price = (new DecimalPipe('en-US').transform(value, '1.0-2') ?? '').replace('.', ',');
    const currencySymbol = (new CurrencyPipe('en-US').transform(1, currency, display, '0.0') ?? '').split('1')[0];
    return this.translateService.stream('FRONTEND.CORE.SHARED.DISPLAY_PRICE.PRICE_CURRENCY_EXC_VAT', {
      price: price,
      currencySymbol: currencySymbol,
    });
  }

  public getSetupFeeString(): Observable<string> {
    return this.translateService.stream('FRONTEND.CORE.SHARED.DISPLAY_PRICE.SETUP_FEE');
  }
}
